import { FC, MouseEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { IconButton, Typography } from '@mui/material';
// import { ReactComponent as PencilSVG } from 'assets/icons/pencil.svg';
import { ReactComponent as TrashSVG } from 'assets/icons/trash.svg';
import { deleteSingleReceiptOtherService } from 'store/bill/actions';
import { getBillReceiptSelector } from 'store/bill/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeConfirmModal, openConfirmModal } from 'store/system/reducer';
import { showToast } from 'store/toastify/reducer';
import { getUserProfileSelector } from 'store/user-service/selectors';
import { Checkbox } from 'components/shared/Checkbox';
import { ProviderAccordion } from 'components/shared/ProviderAccordion';
import { ProviderPreview } from 'components/shared/ProviderPreview';
import { BILL_TYPE } from 'utils/enums';
import {
	checkIsAmountServiceField,
	convertCoinsToUAH,
	convertUAHToCoins,
	decryptData,
	findOtherSelectedServiceInSingleReceipt,
	flatReceiptServiceBillRequestData,
	getErrorMessage,
	getErrorMessageFromTarrifItems,
	getOtherPortmoneAmountFromTariffItems,
	getOtherProviderServicePayment,
	getPortmoneProviderPreviewData,
	getProvideIconByType,
	getServiceTariffItemsList,
} from 'utils/helpers';
import { useDebounce, useOpen } from 'utils/hooks';
import { BillService } from 'utils/services';
import {
	IBillRequestData,
	IPayee,
	IReceiptOtherServiceResponse,
	IServiceBillRequestData,
	IServiceFieldValueResponse,
	ISingleReceiptPayProviderServiceRequest,
	ITariffItemData,
	PaymentMethodsKeys,
	ReceiptOtherServiceTariffItems,
} from 'utils/types';
import { ServiceTariffItem } from '../PaymentProviderItem/EditServiceAmountToPay/ServiceTariffItem';
import { EditServiceAmountToPay } from './EditServiceAmountToPay';
import { EditServiceFieldValuesModal } from './EditServiceFieldValuesModal';
import styles from './index.module.scss';

interface PaymentOtherProviderItemProps {
	data: IReceiptOtherServiceResponse;
	onChange: (serviceList: ISingleReceiptPayProviderServiceRequest[]) => void;
	onExpandChange?: (expanded: boolean) => void;
	selectedServiceList: ISingleReceiptPayProviderServiceRequest[];
	onChangeFee: (serviceId: string | number, amount: number, feeHostCardAmount: number, billId?: string) => void;
	activePaymentMethodGroup?: PaymentMethodsKeys;
}

export const PaymentOtherProviderItem: FC<PaymentOtherProviderItemProps> = ({
	data,
	onChange,
	onExpandChange,
	selectedServiceList,
	onChangeFee,
	activePaymentMethodGroup,
}) => {
	const dispatch = useAppDispatch();

	const params = useParams<{ apartmentAccountId: string }>();

	const profile = useAppSelector(getUserProfileSelector);
	const receipt = useAppSelector(getBillReceiptSelector);

	const editModal = useOpen();

	const [amountToPay, setAmountToPay] = useState<string | null>(null);
	const [feeAmount, setFeeAmount] = useState({ fee: data.fee, feeHostCard: data.fee });
	const [tariffList, setTariffList] = useState<
		{
			tariffItems: ITariffItemData[];
			payee: IPayee;
			description?: string;
		}[]
	>([]);

	const debouncedAmountToPay = useDebounce(amountToPay, 700);

	const isDisabledToChoose = activePaymentMethodGroup ? !data.paymentMethods[activePaymentMethodGroup] : false;

	const flattenedTarrifItems = useMemo(
		() => (Array.isArray(data.tarifItems) ? flatReceiptServiceBillRequestData(data.tarifItems) : []),
		[data.tarifItems]
	);

	useEffect(() => {
		setFeeAmount({
			fee: data.fee,
			feeHostCard: data.fee,
		});
	}, [activePaymentMethodGroup]);

	useEffect(() => {
		if (amountToPay !== null) {
			const payload = selectedServiceList.map((item) =>
				item.serviceId === data.id ? getOtherProviderServicePayment(data, amountToPay) : item
			);
			onChange(payload);
		}
	}, [amountToPay, data.serviceFieldsValues]);

	useEffect(() => {
		if (debouncedAmountToPay !== null && !receipt.data?.isArchived && !isDisabledToChoose) {
			getServiceFee(+convertUAHToCoins(debouncedAmountToPay));
		}
	}, [debouncedAmountToPay, receipt.data?.isArchived, isDisabledToChoose]);

	useEffect(() => {
		let amountToPay: string | null = null;

		const tariffItemsAmount = getOtherPortmoneAmountFromTariffItems(
			data.tarifItems as ReceiptOtherServiceTariffItems | ReceiptOtherServiceTariffItems[]
		);
		if (tariffItemsAmount) {
			amountToPay = tariffItemsAmount.toString();
		} else {
			amountToPay = data.amount - data.payed <= 0 ? '0' : (data.amount - data.payed).toString();
		}

		setAmountToPay(amountToPay);
	}, [data, receipt.data?.isArchived]);

	useEffect(() => {
		if (data.tarifItems && data?.billType === BILL_TYPE.THIRD) {
			const validFlattenedTarrifItems = flattenedTarrifItems.filter((item) => item.payee && item.tariff_items);
			const payload = validFlattenedTarrifItems.map((item) => ({
				tariffItems: getServiceTariffItemsList(item.tariff_items),
				payee: item.payee as IPayee,
				description: item.description,
			}));
			const singleTariffItem = data.tarifItems as IBillRequestData;
			if (!payload.length && singleTariffItem.payee && singleTariffItem.tariff_items) {
				payload.push({
					tariffItems: getServiceTariffItemsList(singleTariffItem.tariff_items),
					payee: singleTariffItem.payee,
					description: singleTariffItem.description,
				});
			}
			setTariffList(payload);
		}
	}, [data.billType, data.tarifItems, flattenedTarrifItems]);

	const getServiceFee = async (amountValue: number): Promise<void> => {
		try {
			if (amountValue <= 0) {
				onChangeFee(data.id, 0, 0, (data.tarifItems as IBillRequestData)?.id);
				setFeeAmount({ fee: 0, feeHostCard: 0 });
				return;
			}

			let feeAmount = 0;
			let feeHostCardAmount = 0;
			if (amountValue) {
				const response = await BillService.getServiceFee(amountValue, data.code, data.provider, true);
				feeAmount = convertCoinsToUAH(response.data.fee);
				feeHostCardAmount = convertCoinsToUAH(response.data.feeHostCard);
			}
			onChangeFee(data.id, feeAmount, feeHostCardAmount, (data.tarifItems as IBillRequestData)?.id);
			setFeeAmount({ fee: feeAmount, feeHostCard: feeHostCardAmount });
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handleSelectService = (event: SyntheticEvent): void => {
		event.stopPropagation();

		const isItemInSelectedServiceList = selectedServiceList.find((item) =>
			findOtherSelectedServiceInSingleReceipt(data, item)
		);
		if (amountToPay !== null) {
			const payload = isItemInSelectedServiceList
				? selectedServiceList.filter((item) => !findOtherSelectedServiceInSingleReceipt(data, item))
				: [...selectedServiceList, getOtherProviderServicePayment(data, amountToPay)];
			onChange(payload);
		}
	};

	const handleChangeService = (amountToPay: string): void => {
		setAmountToPay(amountToPay);
	};

	const handleDeleteService = async (): Promise<void> => {
		try {
			if (profile) {
				await dispatch(
					deleteSingleReceiptOtherService({
						fieldsRequestData: data.serviceFieldsValues.length ? [{ id: data.serviceFieldsValues[0].id }] : [],
						serviceRequestData: {
							apartmentAccountId: +decryptData(params.apartmentAccountId),
							serviceId: data.id,
							userId: profile.userId,
						},
					})
				).unwrap();
				onChange(selectedServiceList.filter((item) => item.serviceId !== data.id));
				dispatch(closeConfirmModal());
				dispatch(showToast({ message: 'Сервіс успішно видалено з ЄК', color: 'success' }));
			}
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handleClickDelete = (event: MouseEvent): void => {
		event.stopPropagation();
		dispatch(
			openConfirmModal({
				title: 'Видалити сервіс',
				subTitle: `Ви впевнені, що бажаєте видалити ${data.title ?? data.name} з інших платежів?`,
				icon: <TrashSVG />,
				submitButtonLabel: 'Так, видалити',
				onSubmit: handleDeleteService,
			})
		);
	};

	// const handleClickEdit = (event: MouseEvent): void => {
	// 	event.stopPropagation();
	// 	editModal.handleOpen();
	// };

	const itemError = getErrorMessageFromTarrifItems(data.tarifItems as IServiceBillRequestData);

	const activePaymentGroupFee = activePaymentMethodGroup === 'isRadaPay' ? feeAmount.feeHostCard : feeAmount.fee;
	const otherBillContent = tariffList.length ? (
		<div className={styles.tariff__list}>
			{tariffList.map((tariffItem, index) => (
				<div key={`${tariffItem.payee._}-${index}`}>
					<Typography className={styles.title} variant="subtitle2" marginBottom="var(--spacing-2)">
						{tariffItem.payee._}
					</Typography>
					{!!tariffItem.description && (
						<Typography sx={{ alignSelf: 'flex-start' }} variant="caption" marginBottom="var(--spacing-2)">
							{tariffItem.description}
						</Typography>
					)}
					{tariffItem.tariffItems.map((item) => (
						<ServiceTariffItem
							key={`tarif-list-portmone-${data.code}-${tariffItem.payee._}-${item.name}`}
							data={item}
						/>
					))}
				</div>
			))}
		</div>
	) : (
		<EditServiceAmountToPay
			fieldName="amount"
			serviceData={data}
			onChange={handleChangeService}
			value={amountToPay ?? '0'}
			feeAmount={activePaymentGroupFee}
			isDisabled={data.valuesDisabled || receipt.isLoading || !!receipt.data?.isArchived}
		/>
	);

	return (
		<>
			<ProviderAccordion
				// In order to close Accordion when it is disabled
				key={`${isDisabledToChoose}`}
				disabled={isDisabledToChoose}
				className={styles.accordion}
				headerClassName={styles.accordion__header}
				disabledClassName={styles.disabled}
				title={
					<div className={styles.header}>
						<ProviderPreview
							className={styles.header__info}
							title={data.title ?? data.name}
							description={getPortmoneProviderPreviewData(data)}
							icon={getProvideIconByType(data.provider, data)}
							code={data.code}
							providerType={data.provider}
							data={data}
						/>
						<div className={styles.header__actions}>
							<IconButton onClick={handleClickDelete}>
								<TrashSVG className={styles.icon__delete} />
							</IconButton>
							{/* https://jira.hostpay.tech/browse/HBILL-376 */}
							{/* <IconButton onClick={handleClickEdit}>
										<PencilSVG className={styles.icon__edit} />
									</IconButton> */}
							{!receipt.data?.isArchived && (
								<Checkbox
									disableRipple
									onClickCapture={handleSelectService}
									id={`${data.id}${data.code}`}
									checked={!!selectedServiceList.find((item) => findOtherSelectedServiceInSingleReceipt(data, item))}
									disabled={receipt.isLoading || receipt.data?.isArchived}
								/>
							)}
						</div>
					</div>
				}
				onChange={(_, expanded: boolean) => onExpandChange?.(expanded)}
			>
				{!isDisabledToChoose &&
					// eslint-disable-next-line no-nested-ternary
					(data.billType === BILL_TYPE.FIRST ? (
						data.serviceFieldsValues
							.filter((item: IServiceFieldValueResponse) => checkIsAmountServiceField(item))
							.map((item: IServiceFieldValueResponse) => (
								<EditServiceAmountToPay
									key={item.id}
									fieldName={item.name}
									serviceData={data}
									onChange={handleChangeService}
									value={amountToPay ?? '0'}
									feeAmount={activePaymentGroupFee}
									isDisabled={data.valuesDisabled || receipt.isLoading || !!receipt.data?.isArchived}
								/>
							))
					) : //  eslint-disable-next-line no-nested-ternary
					data.billType === BILL_TYPE.SECOND ? (
						// eslint-disable-next-line no-nested-ternary
						itemError ? (
							<Typography variant="subtitle2" fontWeight="normal">
								{itemError}
							</Typography>
						) : Object.keys((data.tarifItems as IBillRequestData)?.tariff_items ?? {}).length ? (
							<div className={styles.tariff__list}>
								{getServiceTariffItemsList((data.tarifItems as IBillRequestData).tariff_items).map((item, index) => (
									<ServiceTariffItem key={`tarif-list-portmone-${data.code}-${item.name}-${index}`} data={item} />
								))}
							</div>
						) : (
							<EditServiceAmountToPay
								fieldName="amount"
								serviceData={data}
								onChange={handleChangeService}
								value={amountToPay ?? '0'}
								feeAmount={activePaymentGroupFee}
								isDisabled={data.valuesDisabled || receipt.isLoading || !!receipt.data?.isArchived}
							/>
						)
					) : itemError ? (
						<Typography variant="subtitle2" fontWeight="normal">
							{/* eslint-disable-next-line no-nested-ternary */}
							{itemError}
						</Typography>
					) : (
						otherBillContent
					))}
			</ProviderAccordion>
			<EditServiceFieldValuesModal isOpen={editModal.isOpen} onClose={editModal.handleClose} data={data} />
		</>
	);
};
